import { useState, useEffect, useRef } from 'react';
import './Aichatavatar.css';

const AIChatAvatar = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [avatarMood, setAvatarMood] = useState('happy');
  const [viewMode, setViewMode] = useState('chat'); // 'chat' or 'mascot'

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  const languages = [
    { code: 'en-US', name: 'English', flag: '🇺🇸' },
    { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
    { code: 'ur-PK', name: 'Urdu', flag: '🇵🇰' },
    { code: 'es-ES', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
    { code: 'de-DE', name: 'German', flag: '🇩🇪' },
    { code: 'ar-SA', name: 'Arabic', flag: '🇸🇦' },
    { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko-KR', name: 'Korean', flag: '🇰🇷' }
  ];

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    const welcomeMsg = {
      id: Date.now(),
      text: "Hello! I'm Nexa, your friendly AI assistant. I can help you with anything - birthday plans, gift ideas, celebrations, or just a friendly conversation. What would you like to talk about?",
      sender: 'ai',
      timestamp: new Date()
    };
    setMessages([welcomeMsg]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = selectedLanguage;
    }
  }, [selectedLanguage]);

  const generateAIResponse = async (userMessage) => {
    setIsThinking(true);
    setAvatarMood("thinking");

    const API_URL = import.meta.env.VITE_API_URL || "/api/server";

    try {
      const history = messages
        .map(m => `${m.sender === "user" ? "User" : "AI"}: ${m.text}`)
        .join("\n");

      const prompt = `You are Nexa, a helpful, friendly, smart AI assistant.
Your name is Nexa. You are conversational and natural.

IMPORTANT RULES:
- Never use emojis in your responses (no 😊 🎉 👋 etc)
- Keep responses natural and conversational
- Be direct and helpful
- When speaking in Urdu/Hindi, use proper words (not "a a p", say "aap")
- Don't say emoji names like "smiling face" or "waving hand"
- Just give clean, natural text responses

Conversation so far:
${history}

User: ${userMessage}
Nexa:`;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt })
      });

      if (!response.ok) {
        console.error("API request failed:", response.status, response.statusText);
        return generateContextualResponse(userMessage);
      }

      const data = await response.json();
      console.log("AI response:", data);

      let output = "";
      if (Array.isArray(data)) {
        output = data[0]?.generated_text || "";
      } else if (data.generated_text) {
        output = data.generated_text;
      } else if (data?.outputs?.[0]?.text) {
        output = data.outputs[0].text;
      }

      output = output.replace(prompt, "").trim();

      if (output.length < 2) return generateContextualResponse(userMessage);

      return output;

    } catch (err) {
      console.error("AI ERROR:", err);
      return generateContextualResponse(userMessage);
    } finally {
      setIsThinking(false);
      setAvatarMood("talking");
    }
  };

  const generateContextualResponse = (msg) => {
    const txt = msg.toLowerCase();

    if (txt.includes("gift")) {
      return "Sure! Who is the gift for? (age, gender, interests)";
    }

    if (txt.includes("theme")) {
      return "I can suggest themes! Is it for adults or kids?";
    }

    if (txt.includes("birthday") || txt.includes("celebration")) {
      return "Sounds fun! How many people and where will it be?";
    }

    return "Got it! Tell me a bit more so I can help better.";
  };

 const speakText = (text) => {
  if ('speechSynthesis' in window) {
    synthRef.current.cancel();

    let cleanText = text
      .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
      .replace(/:\w+:/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Phonetic fixes
    cleanText = cleanText
      .replace(/\ba a p\b/gi, "aap")
      .replace(/\bp a r\b/gi, "par")
      .replace(/\bkhana\b/gi, "khaana")
      .replace(/\bpyar\b/gi, "pyaar")
      .replace(/\btheek\b/gi, "theek");

    if (cleanText.length < 2) return;

    const utterance = new SpeechSynthesisUtterance(cleanText);
    const voices = synthRef.current.getVoices();
    let selectedVoice = null;

    // Select best voice
    if (selectedLanguage === "ur-PK") {
      selectedVoice = voices.find(v =>
        v.lang === "hi-IN" &&
        (v.name.includes("Google") || v.name.includes("Microsoft")) &&
        v.name.toLowerCase().includes("male")
      );
      utterance.lang = "hi-IN"; // natural speech
    } 
    else if (selectedLanguage === "hi-IN") {
      selectedVoice = voices.find(v =>
        v.lang === "hi-IN" &&
        (v.name.includes("Google") || v.name.includes("Microsoft"))
      );
    } 
    else {
      selectedVoice = voices.find(v =>
        v.lang === selectedLanguage &&
        (v.name.includes("Google") || v.name.includes("Microsoft"))
      );
    }

    if (selectedVoice) utterance.voice = selectedVoice;

    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setAvatarMood("talking");
    utterance.onend = () => setAvatarMood("happy");

    synthRef.current.speak(utterance);
  }
};


  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    const aiResponse = await generateAIResponse(inputText);

    const aiMessage = {
      id: Date.now() + 1,
      text: aiResponse,
      sender: 'ai',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, aiMessage]);
    speakText(aiResponse);
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Speech recognition error:', error);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
      setAvatarMood('happy');
    }
  };

  const getLatestAIMessage = () => {
    const aiMessages = messages.filter(m => m.sender === 'ai');
    return aiMessages.length > 0 ? aiMessages[aiMessages.length - 1].text : "Hi! I'm Nexa, ready to help you!";
  };

  return (
    <div className={`av-container ${viewMode === 'mascot' ? 'av-view-mascot' : 'av-view-chat'}`}>
      {/* Mode Toggle */}
      <button 
        className="av-mode-toggle"
        onClick={() => setViewMode(viewMode === 'mascot' ? 'chat' : 'mascot')}
      >
        {viewMode === 'mascot' ? '💬 Switch to Chat' : '🤖 Switch to Mascot'}
      </button>

      {viewMode === 'mascot' ? (
        <div className="av-mascot-section">
          <div className="av-mascot-container">
            <div className={`av-speech-bubble ${(isSpeaking || isThinking) ? 'active' : ''}`}>
              {isThinking ? (
                <div className="av-dots"><span></span><span></span><span></span></div>
              ) : (
                <p>{getLatestAIMessage()}</p>
              )}
            </div>

            <div className={`av-mascot-visual ${avatarMood}`}>
              <div className="av-mascot-body">
                <div className="av-mascot-head">
                  <div className="av-eyes">
                    <div className="av-eye left"><div className="av-pupil"></div></div>
                    <div className="av-eye right"><div className="av-pupil"></div></div>
                  </div>
                  <div className={`av-mouth ${avatarMood}`}></div>
                </div>
                <div className="av-arms">
                  <div className={`av-arm left ${isSpeaking ? 'av-waving' : ''}`}></div>
                  <div className={`av-arm right ${isSpeaking ? 'av-waving' : ''}`}></div>
                </div>
                <div className="av-torso"></div>
              </div>
            </div>

            <div className="av-mascot-status">
              <span>{isThinking ? 'Thinking...' : isSpeaking ? 'Speaking...' : 'Ready to help!'}</span>
              <select 
                value={selectedLanguage} 
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="av-lang-select"
              >
                {languages.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      ) : (
        <div className="av-chat-section">
          <div className="av-chat-header">
            <div className="av-header-left">
              <div className="av-header-avatar">{avatarMood === 'thinking' ? '🤔' : '😊'}</div>
              <div>
                <h1 className="av-header-name">Nexa</h1>
                <p className="av-header-status">{isThinking ? 'Thinking...' : 'Online'}</p>
              </div>
            </div>
            <select 
              value={selectedLanguage} 
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="av-lang-select"
            >
              {languages.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
            </select>
          </div>

          <div className="av-messages-list">
            {messages.map((m) => (
              <div key={m.id} className={`av-msg-row ${m.sender === 'user' ? 'av-user' : 'av-ai'}`}>
                <div className="av-msg-bubble">
                  {m.text}
                  <div className="av-msg-time">{m.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            ))}
            {isThinking && (
              <div className="av-msg-row av-ai">
                <div className="av-msg-bubble">
                  <div className="av-dots"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Shared Input Area */}
      <div className="av-input-container">
        <div className="av-suggestions-row">
          {['🎁 Gift Ideas', '🎉 Party Plans', '✨ Celebrations'].map((s, i) => (
            <button key={i} className="av-suggestion-btn" onClick={() => setInputText(s.split(' ')[1])}>
              {s}
            </button>
          ))}
        </div>
        <div className="av-input-wrapper">
          <textarea
            className="av-input-field"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type or speak..."
            rows="1"
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
          />
          <div className="av-input-actions">
            <button className="av-icon-btn" onClick={isListening ? stopListening : startListening}>
              {isListening ? '🛑' : '🎤'}
            </button>
            <button className="av-icon-btn av-send-btn" onClick={handleSendMessage} disabled={!inputText.trim()}>
              ➤
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatAvatar;

